import { spouseIds } from "@/lib/spouses";
export function computeDepths(persons) {
  const byId = {};
  persons.forEach((p) => (byId[p.id] = p));
  const cache = {};
  const getDepth = (id, seen = new Set()) => {
    if (cache[id] !== undefined) return cache[id];
    if (seen.has(id)) return 0;
    seen.add(id);
    const p = byId[id];
    if (!p) return 0;
    const parents = [p.father_id, p.mother_id].filter((x) => x && byId[x]);
    let d = 0;
    if (parents.length) d = Math.max(...parents.map((pid) => getDepth(pid, seen))) + 1;
    cache[id] = d;
    return d;
  };
  persons.forEach((p) => getDepth(p.id));

  // spouse depth propagation: a married-in spouse (no parents here) takes partner's generation
  let changed = true;
  while (changed) {
    changed = false;
    persons.forEach((p) => {
      spouseIds(p).forEach((id) => {
        const sp = byId[id];
        if (!sp) return;
        const hasParents = (x) => (x.father_id && byId[x.father_id]) || (x.mother_id && byId[x.mother_id]);
        if (!hasParents(p) && cache[p.id] < cache[sp.id]) {
          cache[p.id] = cache[sp.id];
          changed = true;
        }
        if (!hasParents(sp) && cache[sp.id] < cache[p.id]) {
          cache[sp.id] = cache[p.id];
          changed = true;
        }
      });
    });
  }

  const maxDepth = persons.length ? Math.max(0, ...persons.map((p) => cache[p.id] || 0)) : 0;
  return { depthById: cache, maxDepth };
}

// Group persons into couple units (person + spouse) with a stable key.
export function buildUnits(persons) {
  const byId = {};
  persons.forEach((p) => (byId[p.id] = p));
  const { depthById } = computeDepths(persons);
  const unitOf = {};
  const units = [];
  persons.forEach((p) => {
    if (unitOf[p.id]) return;
    const members = [];
    const pending = [p];
    const seen = new Set();
    while (pending.length) {
      const member = pending.pop();
      if (!member || seen.has(member.id) || unitOf[member.id]) continue;
      seen.add(member.id);
      members.push(member);
      spouseIds(member).forEach((id) => pending.push(byId[id]));
      persons.filter((other) => spouseIds(other).includes(member.id)).forEach((other) => pending.push(other));
    }
    members.sort((a, b) => (b.gender === "Male" ? 1 : 0) - (a.gender === "Male" ? 1 : 0));
    const u = {
      members,
      key: members.map((m) => m.id).sort().join("|"),
      depth: Math.max(...members.map((m) => depthById[m.id] || 0)),
    };
    units.push(u);
    members.forEach((m) => (unitOf[m.id] = u));
  });
  const childrenOf = new Map();
  persons.forEach((p) => {
    const cu = unitOf[p.id];
    [p.father_id, p.mother_id].forEach((pid) => {
      const pu = pid ? unitOf[pid] : null;
      if (pu && pu !== cu) {
        if (!childrenOf.has(pu)) childrenOf.set(pu, new Set());
        childrenOf.get(pu).add(cu);
      }
    });
  });
  return { byId, unitOf, units, childrenOf };
}

// Count each family unit once, including spouses and shared descendants.
export function largestTreeRoot(persons) {
  const { units, childrenOf } = buildUnits(persons);
  const children = new Set([...childrenOf.values()].flatMap((items) => [...items]));
  const roots = units.filter((unit) => !children.has(unit));
  let largest = null;
  let largestSize = 0;
  for (const root of roots.length ? roots : units) {
    const seen = new Set();
    const stack = [root];
    let size = 0;
    while (stack.length) {
      const unit = stack.pop();
      if (seen.has(unit)) continue;
      seen.add(unit);
      size += unit.members.length;
      stack.push(...(childrenOf.get(unit) || []));
    }
    if (size > largestSize) {
      largest = root.members[0].id;
      largestSize = size;
    }
  }
  return largest;
}

// Hide ancestors and their other branches, retaining the selected family and descendants.
export function collapseAncestors(persons, collapsedKeys = new Set()) {
  const { units, unitOf, childrenOf } = buildUnits(persons);
  const byKey = new Map(units.map((u) => [u.key, u]));
  const descendants = (roots) => {
    const seen = new Set();
    const stack = [...roots];
    while (stack.length) {
      const unit = stack.pop();
      if (!unit || seen.has(unit.key)) continue;
      seen.add(unit.key);
      stack.push(...(childrenOf.get(unit) || []));
    }
    return seen;
  };
  const hidden = new Set();
  units.filter((u) => collapsedKeys.has(u.key)).forEach((u) => {
    const retained = descendants([u]);
    const ancestors = ancestorKeys(persons, u.members[0].id).map((key) => byKey.get(key));
    descendants(ancestors).forEach((key) => {
      if (!retained.has(key)) hidden.add(key);
    });
  });
  return persons.filter((p) => !hidden.has(unitOf[p.id].key));
}

// Family keys of every unit that has descendants (for Collapse All).
export function computeCollapseAllKeys(persons) {
  const { units, childrenOf } = buildUnits(persons);
  return units.filter((u) => (childrenOf.get(u) || new Set()).size > 0).map((u) => u.key);
}

// Family keys of all ancestor units of a person (for auto-expand before panning).
export function ancestorKeys(persons, personId) {
  const { unitOf } = buildUnits(persons);
  const keys = [];
  const seen = new Set();
  const stack = [unitOf[personId]].filter(Boolean);
  while (stack.length) {
    const u = stack.pop();
    if (!u || seen.has(u.key)) continue;
    seen.add(u.key);
    u.members.forEach((m) =>
      [m.father_id, m.mother_id].forEach((pid) => {
        const pu = pid ? unitOf[pid] : null;
        if (pu && pu !== u && !seen.has(pu.key)) {
          keys.push(pu.key);
          stack.push(pu);
        }
      })
    );
  }
  return keys;
}


// The linked member is generation zero; children are descendant generation one.
export function linkedMemberView(persons, personId, generations = 4) {
  const { unitOf, childrenOf } = buildUnits(persons);
  const root = unitOf[personId];
  if (!root) return { ancestors: new Set(), descendants: new Set() };
  const descendants = new Set();
  const seen = new Set([root.key]);
  const queue = [[root, 0]];
  for (let i = 0; i < queue.length; i++) {
    const [unit, depth] = queue[i];
    const children = childrenOf.get(unit) || new Set();
    if (depth >= generations) {
      if (children.size) descendants.add(unit.key);
      continue;
    }
    children.forEach((child) => {
      if (!seen.has(child.key)) {
        seen.add(child.key);
        queue.push([child, depth + 1]);
      }
    });
  }
  return { ancestors: new Set([root.key]), descendants };
}


export function toggleAncestorLayer(persons, collapsedKeys, familyKey) {
  const next = new Set(collapsedKeys);
  if (!next.has(familyKey)) {
    next.add(familyKey);
    return next;
  }
  next.delete(familyKey);
  const { units, unitOf } = buildUnits(persons);
  const unit = units.find((item) => item.key === familyKey);
  // Move the collapsed boundary to the immediate parents, keeping grandparents hidden.
  unit?.members.forEach((member) => {
    [member.father_id, member.mother_id].forEach((id) => {
      const parent = unitOf[id];
      if (parent && parent !== unit) next.add(parent.key);
    });
  });
  return next;
}
